import { Injectable } from '@angular/core';
import { HttpClient} from '@angular/common/http';
import { Observable, ReplaySubject} from 'rxjs';
import { first, take } from 'rxjs/operators';
import { Uuid } from '../interface/response/uuid';
import { CountedAndPaginated } from '../interface/response/counted-and-paginated';
import {FilterService} from "./filter.service";
import {License} from "../interface/license";
import {Device} from "../interface/device";
import {ListResponse} from "../interface/response/list-response";
import {Document as DocumentInterface} from "../interface/document";

@Injectable({
  providedIn: 'root'
})
export class LicenseService {
  private readonly licensesListSubject: ReplaySubject<CountedAndPaginated<License>>
    = new ReplaySubject<CountedAndPaginated<License>>(1);

  private readonly devicesListSubject: ReplaySubject<CountedAndPaginated<Device>>
    = new ReplaySubject<CountedAndPaginated<Device>>(1);

  private readonly deviceNotCountedListSubject: ReplaySubject<ListResponse<Device>> = new ReplaySubject<ListResponse<Device>>(1);


  constructor(
    private httpClient: HttpClient,
    private filterService: FilterService,
  ) {}

  public loadList(
    page: number, pageSize: number, filters: object, sort?: { sortBy?: string; sortOrder?: string }
  ): void {
    const sortBy = sort?.sortBy || 'createdAt';
    const sortOrder = sort?.sortOrder || 'desc';

    this.httpClient.get(`/rest/crm/license?page=${page + 1}` + `&pageSize=${pageSize}&sortBy=${sortBy}&sortOrder=${sortOrder}&` +
      this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: CountedAndPaginated<License>) => {
        this.licensesListSubject.next(result);
      });
  }

  sendForm(formData: object, licenseId: string): Observable<any> {
    if (!licenseId) {
      return this.httpClient.post(`/rest/crm/license`, formData)
        .pipe(take(1));
    } else {
      return this.httpClient.put(`/rest/crm/license/${licenseId}`, formData)
        .pipe(take(1));
    }
  }

  public getLicense(uuid: string): Observable<License> {
    return this.httpClient.get(`/rest/crm/license/${uuid}`)
      .pipe(first()) as Observable<License>;
  }

  public delete(uuid: string): Observable<any> {
    return this.httpClient.delete(`/rest/crm/license/${uuid}`)
      .pipe(first());
  }

  downloadReport(filters: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/license/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public getLicensesListSubject(): ReplaySubject<CountedAndPaginated<License>> {
    return this.licensesListSubject;
  }

  public getDeviceListSubject(): ReplaySubject<CountedAndPaginated<Device>> {
    return this.devicesListSubject;
  }

  addDevice(formData: object, deviceId: string): Observable<any> {
    if (!deviceId) {
      return this.httpClient.post(`/rest/crm/device`, formData)
        .pipe(take(1));
    } else {
      return this.httpClient.put(`/rest/crm/device/${deviceId}`, formData)
        .pipe(take(1));
    }
  }

  public loadDeviceList(page: number, pageSize: number, filters: object): void {
    this.httpClient.get(`/rest/crm/device?page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(
        first(),
      )
      .subscribe((result: CountedAndPaginated<Device>) => {
        this.devicesListSubject.next(result);
      });
  }

  public loadDeviceNotCountedList(page: number, pageSize: number, filters: object, observable?: ReplaySubject<ListResponse<Device>>): void {
    this.httpClient
      .get(`/rest/crm/device?disableCount=1&page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: ListResponse<Device>) => {
        if (observable) {
          observable.next(result);
        } else {
          this.deviceNotCountedListSubject.next(result);
        }
      });
  }

  public getDevice(uuid: string): Observable<Device> {
    return this.httpClient.get(`/rest/crm/device/${uuid}`)
      .pipe(first()) as Observable<Device>;
  }

  public getDeviceDocuments(deviceId: string, typeId?: number): Observable<ListResponse<DocumentInterface>> {
    return this.httpClient.get(`/rest/crm/device/${deviceId}/document${typeId ?  '/' + typeId : ''}`)
      .pipe(first()) as Observable<ListResponse<DocumentInterface>>;
  }

  downloadDeviceReport(filters: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/device/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public deleteDevice(uuid: string): Observable<any> {
    return this.httpClient.delete(`/rest/crm/device/${uuid}`)
      .pipe(first());
  }

  public getDeviceLicenses(uuid: string): Observable<ListResponse<License>> {
    return this.httpClient.get(`/rest/crm/device/${uuid}/licenses`)
      .pipe(first()) as Observable<ListResponse<License>>;
  }

}
